// src/components/blog/features/TrendingTopics.tsx

'use client'

import { motion } from 'motion/react'
import type { Topic } from '~/lib/blog/types'
import Link from 'next/link'
import { cn } from '~/lib/utils'
import { IconComponent } from '~/components/shared/IconComponent'
import { getIconComponent } from '~/lib/blog/utils/icon-map'
import { usePathname } from 'next/navigation'

export interface TopicStats extends Topic {
  postCount: number
  recentPosts: number
}

interface TrendingTopicsProps {
  topics: TopicStats[]
  className?: string
  showViewAll?: boolean
}

export function TrendingTopics({ topics, className, showViewAll = true }: TrendingTopicsProps): JSX.Element {
  const pathname = usePathname()
  const isTopicsPage = pathname === '/blog/topics'

  return (
    <section className={cn("space-y-6", className)}>
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
          {isTopicsPage ? 'All Topics' : 'Trending Topics'}
        </h2>
        {showViewAll && !isTopicsPage && (
          <Link
            href="/blog/topics"
            className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
          >
            View all topics
            <IconComponent name="RiArrowRightLine" className="ml-1 inline-block h-4 w-4" />
          </Link>
        )}
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {topics.map((topic, index) => {
          const IconComponent = getIconComponent(topic.iconName)
          return (
            <motion.div
              key={topic.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                href={topic.href}
                className="group relative flex flex-col overflow-hidden rounded-2xl bg-white p-6 shadow-sm hover:shadow-md dark:bg-gray-800 transition-all duration-200"
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600/10 dark:bg-blue-400/10">
                    <IconComponent className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">
                      {topic.title}
                    </h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {topic.postCount} {topic.postCount === 1 ? 'post' : 'posts'}
                    </p>
                  </div>
                </div>
                {topic.description && (
                  <p className="mt-4 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                    {topic.description}
                  </p>
                )}
              </Link>
            </motion.div>
          )
        })}
      </div>
    </section>
  )
} 